<script setup lang="ts">
import { onMounted, ref, toRefs } from 'vue';
import { storeToRefs } from 'pinia';
import { useAttendanceStore, useOrganizationStore } from '@/store';
import LowAttendance from './LowAttendance.vue';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { useToast } from 'primevue/usetoast';

const dates = ref<Date[]>([]);
const attendanceStore = useAttendanceStore();
const organizationStore = useOrganizationStore();
const { start_at, end_at, loading } = storeToRefs(attendanceStore);
const { organizationsIdsSelected } = storeToRefs(organizationStore);
const toast = useToast();

interface EvaluationPropsInterface {
  organizationsIds?: number[];
}

const props = defineProps<EvaluationPropsInterface>();
const { organizationsIds } = toRefs(props);

onMounted(() => init());

const init = async () => {
  attendanceStore.filteredStudent = null;
  setDefaultDates();

  const organizationIdToSee = !can([PERMISSIONS.WIDGET_EVALUATION_ALL_BY_ORGANIZATIONS]) ? organizationsIds?.value : [];
  const organizationsIdToEvaluate: number[] | undefined =
    organizationsIdsSelected.value.length == 0 ? organizationIdToSee : organizationsIdsSelected.value;

  await attendanceStore.getAllAttendance(organizationsIdToEvaluate || []);
};

const setDefaultDates = () => {
  if (!can([PERMISSIONS.WIDGET_ATTENDANCE_LOW_ATTENDANCE, PERMISSIONS.WIDGET_ATTENDANCE_ALL])) {
    doToast(true, 'Insufficient permissions to view low attendance data.');
    return;
  }

  const userDateInterval = [-28, 0];
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + userDateInterval[1]);
  start.setDate(start.getDate() + userDateInterval[0]);
  dates.value = [start, end];
  attendanceStore.setDates(start.toJSON().split('T')[0], end.toJSON().split('T')[0]);
};

const doToast = (error = false, msg: string) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: msg,
    life: 5000,
  });
};

const onDateRangeChange = async (dateArray: Date[]) => {
  const start_date = dateArray[0]?.toJSON()?.split('T')[0];
  const end_date = dateArray[1]?.toJSON()?.split('T')[0];

  if (start_date && end_date) {
    attendanceStore.setDates(start_date, end_date);
    await attendanceStore.onDateRangeChange(organizationsIdsSelected.value);
  }
};
</script>

<template>
  <Card class="page-content-half flex flex-column">
    <template #header>
      <span class="material-icons text-color-blue mr-2">event</span>
      <h2>Attendance</h2>
    </template>
    <template #content>
      <div
        v-if="(start_at || !loading) && can([PERMISSIONS.DASHBOARD_OVERVIEW_ATTENDACE_WIDGET_DATE_VIEW])"
        class="w-full p-input-icon-left mb-3"
      >
        <AppDateRangePicker v-model="dates" class="w-full" :value="dates" @change="onDateRangeChange" />
      </div>
      <Skeleton v-else shape="rectangle" class="mb-3" style="min-height: 36px" />
      <LowAttendance v-if="can([PERMISSIONS.WIDGET_ATTENDANCE_LOW_ATTENDANCE]) && start_at && end_at" />
    </template>
  </Card>
</template>

<style scoped lang="scss">
.daterangepicker-container {
  max-width: none !important;
  :deep(.p-inputtext) {
    font-weight: bold;
    padding-left: 30px;
  }
}
:deep(.p-card-body) {
  flex: 1;
}
:deep(.p-card-content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
